// src/features/goal/config/categoryMapping.ts
//
// Objective Category 映射配置（TypeScript 版本）
//
// 設計原則：
// - 與後端配置保持一致（functions/src/config/categoryMapping.js）
// - 單一真相來源（Single Source of Truth）
// - Legacy category 映射規則集中管理
//
// 使用場景：
// 1. 從後端接收的資料可能包含 legacy category，需要映射顯示
// 2. 確保前後端使用一致的映射規則
// 3. UI 過濾和驗證 category 值

import type { ObjectiveCategory } from '../types/objective';
import { logger } from '../../../utils/logger';

/**
 * Legacy Category 映射表
 *
 * 將舊的 category 值映射到新的標準 category
 * ⚠️ 此配置必須與後端 functions/src/config/categoryMapping.js 保持一致
 *
 * @type {Record<string, ObjectiveCategory>}
 */
export const LEGACY_CATEGORY_MAPPING: Partial<Record<string, ObjectiveCategory>> = {
  // 學習相關 → growth
  'learning': 'growth',
  'education': 'growth',
  'skill': 'growth',
  'personal_growth': 'growth',

  // 財務相關 → finance
  'finance_investment': 'finance',
  'investment': 'finance',
  'money': 'finance',

  // 工作相關保持不變
  'work': 'work',
  'career': 'work',

  // 健康相關保持不變
  'health': 'health',
  'fitness': 'health',
  'wellness': 'health',

  // 社交相關 → social
  'relationship': 'social',
  'family': 'social',
  'friends': 'social',

  // 環境相關保持不變
  'environment': 'environment',
  'home': 'environment',

  // 娛樂相關 → leisure
  'entertainment': 'leisure',
  'hobby': 'leisure',
  'fun': 'leisure',

  // 感情相關 → romance
  'romance': 'romance',
  'love': 'romance',
  'dating': 'romance',
} as const;

/**
 * 標準 Category 列表
 *
 * 當前系統支援的所有有效 category（不包含 legacy）
 *
 * @type {ObjectiveCategory[]}
 */
export const VALID_CATEGORIES: ObjectiveCategory[] = [
  'work',
  'health',
  'growth',
  'finance',
  'romance',
  'social',
  'leisure',
  'environment',
];

/**
 * 預設 Category
 *
 * 當無法判斷或映射失敗時使用
 * 決策理由：個人成長是最廣泛的分類，適合作為 fallback
 *
 * @type {ObjectiveCategory}
 */
export const DEFAULT_OBJECTIVE_CATEGORY: ObjectiveCategory = 'growth';

/**
 * 映射 Legacy Category 到標準 Category
 *
 * @param category - 需要映射的 category（可能是 legacy 或標準值）
 * @param options - 可選配置
 * @param options.logMapping - 是否記錄映射行為（預設 false）
 * @returns 映射後的標準 category
 *
 * @example
 * ```typescript
 * mapLegacyCategory('learning') // => 'growth'
 * mapLegacyCategory('work') // => 'work'
 * mapLegacyCategory('invalid') // => 'growth'
 * mapLegacyCategory('learning', { logMapping: true }) // => 'growth' + log
 * ```
 */
export function mapLegacyCategory(
  category: string | undefined | null,
  options: { logMapping?: boolean } = {}
): ObjectiveCategory {
  const { logMapping = false } = options;

  // 如果沒有提供 category，返回預設值
  if (!category) {
    return DEFAULT_OBJECTIVE_CATEGORY;
  }

  // 如果已經是標準 category（類型守衛）
  if (isValidCategory(category)) {
    return category as ObjectiveCategory;
  }

  // 查找映射
  const mapped = LEGACY_CATEGORY_MAPPING[category];

  // 如果找到映射，記錄並返回
  if (mapped) {
    if (logMapping) {
      logger.warn('Legacy category detected', { category, mapped });
    }
    return mapped;
  }

  // 無法識別，返回預設值
  if (logMapping) {
    logger.warn('Unknown category, using default', {
      category,
      default: DEFAULT_OBJECTIVE_CATEGORY
    });
  }

  return DEFAULT_OBJECTIVE_CATEGORY;
}

/**
 * 驗證 Category 是否為標準值
 *
 * @param category - 需要驗證的 category
 * @returns 是否為標準 category
 *
 * @example
 * ```typescript
 * isValidCategory('work') // => true
 * isValidCategory('learning') // => false
 * isValidCategory('family') // => false (legacy)
 * ```
 */
export function isValidCategory(category: string): category is ObjectiveCategory {
  return VALID_CATEGORIES.includes(category as ObjectiveCategory);
}

/**
 * 類型安全的 Category 驗證與映射
 *
 * 結合驗證和映射邏輯，確保返回的一定是有效的 ObjectiveCategory
 *
 * @param category - 需要處理的 category
 * @returns 有效的 ObjectiveCategory
 *
 * @example
 * ```typescript
 * ensureValidCategory('learning') // => 'growth'
 * ensureValidCategory('work') // => 'work'
 * ensureValidCategory(undefined) // => 'growth'
 * ```
 */
export function ensureValidCategory(
  category: string | undefined | null
): ObjectiveCategory {
  return mapLegacyCategory(category, { logMapping: true });
}
